<template>
    <label class="container" :class="`style-${style_type}`">
    <input
        class="input"
        v-bind="$attrs"
        type="checkbox"
        :checked="checked"
        @change="$emit('update:checked', $event.target.checked)"
    >
    <span class="label mr-1" v-if="label_left">{{ label_left }}</span>
    <span class="switch"></span>
    <span class="label ml-1" v-if="label_right">{{ label_right }}</span>
</label>
</template>

<script setup>
    defineOptions({
        inheritAttrs: false
    })

    const props = defineProps({
        label_left: String,
        label_right: String,
        checked: Boolean,
        style_type: { type: String, default: 'default' }
    })
</script>

<style lang="scss" scoped>
.container {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ml-1 {
    margin-left: 1rem;
}

.input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

.switch {
    --switch-container-width: 50px;
    --switch-size: calc(var(--switch-container-width) / 2);

    /* Default color variables */
    --switch-bg-off: #c42020;
    --switch-bg-on: #4fd1c5;
    --switch-border-off: #c42020;
    --switch-border-on: #4fd1c5;
    --switch-focus-border: #ffa500;

    display: flex;
    align-items: center;
    position: relative;
    height: var(--switch-size);
    flex-basis: var(--switch-container-width);
    border: 2px solid transparent;
    border-radius: var(--switch-size);
    background-color: var(--switch-bg-off);
    transition: background-color 0.25s ease-in-out;
    flex-shrink: 0;
    box-sizing: border-box;
}

.switch::before {
    content: "";
    position: absolute;
    left: -2px;
    height: calc(var(--switch-size) - 4px);
    width: calc(var(--switch-size) - 4px);
    border: 2px solid var(--switch-border-off);
    border-radius: 50%;
    background-color: white;
    transition: transform 0.375s ease-in-out, border-color 0.25s ease-in-out;
}

/* Checked state */
.container:has(.input:checked) .switch {
    background-color: var(--switch-bg-on);
}

.container:has(.input:checked) .switch::before {
    border-color: var(--switch-border-on);
    transform: translateX(calc(var(--switch-container-width) - var(--switch-size)));
}

/* Focus states */
.container:has(.input:focus) .switch::before {
    border-color: var(--switch-border-off);
}

.container:has(.input:focus:checked) .switch::before {
    border-color: var(--switch-border-on);
}

.container:has(.input:focus) .switch {
    border: 2px solid var(--switch-focus-border);
}

/* Disabled state */
.container:has(.input:disabled) .switch {
    background-color: #cbd5e0;
    cursor: no-drop;
}

.container:has(.input:disabled) .switch::before {
    background-color: #a0aec0;
    border-color: #a0aec0;
}

/* ------------------- Style Types ------------------- */
/* Discreet style */
.style-discreet .switch {
    --switch-bg-off: #cbd5e0;
    --switch-bg-on: #004a8c;
    --switch-border-off: #cbd5e0;
    --switch-border-on: #004a8c;
}
</style>